import { GridNine, RectangleDashed } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ItemImage, SkupremeModal } from '../../components';
import {
  CenterDropdownStyle,
  ColoredPill,
  HoverBorderButton,
  MuteText,
  StyledButton,
} from '../../elements';
import { AllocationService, theme, ToastMessage } from '../../utility';

const NameContainer = styled.span`
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;

const ItemContaienr = styled.div`
  max-width: 80%;
`;

const getColor = color => {
  if (['rgb(255,255,255)', '#FFFFFF', '#ffffff'].includes(color)) {
    return theme.colors.black;
  }

  if (color) {
    return color;
  }

  return theme.colors.box;
};

export function ChangeLocationPopup({ open, item, warehouse, onClose, refresh }) {
  const [locationLoading, setLocationLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [moveTo, setMoveTo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (warehouse?._id && item) {
      fetchLocations();
    } else {
      setLocations([]);
    }
  }, [warehouse, item]);

  const fetchLocations = async () => {
    try {
      setLocationLoading(true);
      const params = {
        warehouseId: warehouse._id,
        allocationId: item.id,
      };
      const res = await AllocationService.listLocation(item.id, params);
      setLocations(res?.locations);
      setMoveTo(res?.locations[0]?._id);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLocationLoading(false);
    }
  };

  const options = useMemo(
    () =>
      locations?.map(location => ({
        key: location?._id,
        value: location?._id,
        text: (
          <div className="d-flex align-items-center">
            <ColoredPill color={location?.zoneId?.color}>
              <RectangleDashed color={location?.zoneId?.color} />
              <span>{location?.zoneId?.code}</span>
            </ColoredPill>
            <div className="mx-4 d-flex align-items-center">
              <GridNine
                color={getColor(location?.zoneId?.color)}
                className="mr-1"
                size={14}
              />
              <span>{location?.rackId?.code}</span>
            </div>
            <div className="d-flex align-items-center">
              <RectangleDashed
                color={getColor(location?.zoneId?.color)}
                className="mr-1"
                size={14}
              />
              <span>{location?.code}</span>
            </div>
          </div>
        ),
        item: location,
        content: (
          <div className="d-flex align-items-center">
            <ColoredPill color={location?.zoneId?.color}>
              <RectangleDashed color={location?.zoneId?.color} />
              <span>{location?.zoneId?.code}</span>
            </ColoredPill>
            <div className="mx-4 d-flex align-items-center">
              <GridNine
                color={getColor(location?.zoneId?.color)}
                className="mr-1"
                size={14}
              />
              <span>{location?.rackId?.code}</span>
            </div>
            <div className="d-flex align-items-center">
              <RectangleDashed
                color={getColor(location?.zoneId?.color)}
                className="mr-1"
                size={14}
              />
              <span>{location?.code}</span>
            </div>
          </div>
        ),
      })),
    [locations],
  );

  const changeAllocation = async () => {
    try {
      setLoading(true);
      const data = {
        locationId: moveTo,
      };
      await AllocationService.changeAllocation(item.id, data);
      ToastMessage.success('Location changed successfully');
      onClose();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SkupremeModal
      size="mini"
      open={open}
      title="Change Allocated Location"
      onClose={onClose}
    >
      <div className="d-flex mb-2 align-items-start">
        <ItemImage
          item={item?.inventory?.product}
          imageKey="images"
          displayImageKey="display_image"
          size="mini"
        />
        <ItemContaienr className="ml-3">
          <NameContainer>{item?.inventory?.product?.name}</NameContainer>
          <div>
            <MuteText className="mr-1">SKU</MuteText>
            <span>{item?.inventory?.product?.sku}</span>
          </div>
        </ItemContaienr>
      </div>
      <div>
        <MuteText>Current Allocated Location</MuteText>
        <div className="d-flex align-items-center">
          <ColoredPill color={item?.inventory?.zone?.color}>
            <RectangleDashed color={item?.inventory?.zone?.color} />
            <span>{item?.inventory?.zone?.code}</span>
          </ColoredPill>
          <div className="mx-4 d-flex align-items-center">
            <GridNine
              color={getColor(item?.inventory?.zone?.color)}
              className="mr-1"
              size={14}
            />
            <span>{item?.inventory?.rack?.code}</span>
          </div>
          <div className="d-flex align-items-center">
            <RectangleDashed
              color={getColor(item?.inventory?.zone?.color)}
              className="mr-1"
              size={14}
            />
            <span>{item?.inventory?.location?.code}</span>
          </div>
        </div>
      </div>
      <div className="my-3">
        <MuteText>New Location</MuteText>
        <CenterDropdownStyle
          options={options}
          selection
          loading={locationLoading}
          value={moveTo}
          onChange={(e, { value }) => setMoveTo(value)}
        />
      </div>
      <div className="d-flex justify-content-end pt-4">
        <HoverBorderButton disabled={loading} className="mr-2" onClick={onClose}>
          Cancel
        </HoverBorderButton>
        <StyledButton loading={loading} onClick={changeAllocation}>
          Confirm
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
